import toast from "react-hot-toast";

export const RECORD_LIMIT = 10;
export const SECURITY_REGEX = /^[a-zA-Z0-9\s.,\-()'&?%]*$/;
export const SECURITY_MESSAGE = "Special characters like < > / \\ ; : \" are not allowed for security";
export const ToastPosition = {
  TOP_LEFT: "top-left",
  TOP_CENTER: "top-center",
  TOP_RIGHT: "top-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_CENTER: "bottom-center",
  BOTTOM_RIGHT: "bottom-right",
} as const;

export const ToastType = {
  SUCCESS: "success",
  ERROR: "error",
} as const;

export type ToastTypeKey = keyof typeof ToastType;
export type ToastPositionType = keyof typeof ToastPosition;

export const AITopUpTypes = {
  BUILD_SURVEY_USING_AI: "BUILD_SURVEY_USING_AI",
  QUIZ_RESULT_USING_AI: "QUIZ_RESULT_USING_AI",
  EXECUTIVE_REPORT: "Executive_Report",
} as const;

export const AzureErrorCode = {
  INTERACTION_IN_PROGRESS: "interaction_in_progress", // Authentication flow is already in progress
  POPUP_CLOSED_BY_USER: "popup_closed_by_user",       // User closed the popup before completing the login
  USER_CANCELLED: "user_cancelled",                  // User canceled the operation
  NO_ACCOUNT: "no_account",                          // No account available for silent login
  TOKEN_ACQUISITION_FAILED: "token_acquisition_failed", // Failed to acquire token
  INVALID_GRANT: "invalid_grant",                    // Invalid credentials or session expired
  NETWORK_ERROR: "network_error",                    // Network issue during authentication
  CLIENT_AUTH_ERROR: "client_authentication_error",  // Client authentication failure
  TIMEOUT: "timeout",                                // Operation timed out
  UNEXPECTED_ERROR: "unexpected_error",              // Catch-all for unexpected errors
} as const;

export const AzureErrorMessages = {
  [AzureErrorCode.INTERACTION_IN_PROGRESS]: "A login process is already in progress. Please complete it before trying again.",
  [AzureErrorCode.POPUP_CLOSED_BY_USER]: "Login popup was closed. Please try again.",
  [AzureErrorCode.USER_CANCELLED]: "Login popup was closed. Please try again..",
  [AzureErrorCode.NETWORK_ERROR]: "A network error occurred. Please check your internet connection and try again.",
  [AzureErrorCode.INVALID_GRANT]: "Your session has expired or the provided credentials are invalid. Please log in again.",
  [AzureErrorCode.CLIENT_AUTH_ERROR]: "An authentication error occurred. Please contact support if this persists.",
  [AzureErrorCode.TIMEOUT]: "The login process timed out. Please try again.",
  [AzureErrorCode.TOKEN_ACQUISITION_FAILED]: "Failed to acquire the authentication token. Please refresh the page and try again.",
  [AzureErrorCode.NO_ACCOUNT]: "No user account is available for this operation. Please ensure you are logged in.",
  [AzureErrorCode.UNEXPECTED_ERROR]: "An unexpected error occurred during login. Please try again or contact support.",
};
export const getErrorMessage = (errorCode: keyof typeof AzureErrorMessages) => {
  return AzureErrorMessages[errorCode] || AzureErrorMessages[AzureErrorCode.UNEXPECTED_ERROR];
};

export const RouteFlagConstant = {
  FORMS: 1,
  QUIZ: 2,
  POLL: 3,
} as const;

/**
 * Response type names that are allowed when adding questions to a Quiz.
 * Quiz only supports Single Choice, Multi Choice, and Text-based questions.
 */
export const QUIZ_ALLOWED_RESPONSE_TYPES: string[] = [
  "Radio Button",
  "Yes or No",
  "DropDown",
  "Single Choice",
  "CheckBox",
  "Multi-Select DropDown",
  "TextBox",
  "TextArea",
  "Text Area",
];

export const SystemEmailTemplates = {
  SurveyPublished: 1,
  NewUserExternal: 2,
  ResetPassword: 3,
  ReminderSurvey: 4,
  ThankYouSurvey: 5,
  ThankYouQuiz: 6,
  QuizPublished: 7,
} as const;

export const SystemEmailTemplatesOptions = [
  { label: 'Survey Published', value: 1 },
  { label: 'New User External', value: 2 },
  { label: 'Reset Password', value: 3 },
  { label: 'Reminder Survey', value: 4 },
  { label: 'Thank You Survey', value: 5 },
  { label: 'Thank You Quiz', value: 6 },
  { label: 'Quiz Published', value: 7 },
];

interface ShowToastProps {
  message: string;
  type: ToastTypeKey;
  position?: ToastPositionType;
}

export const ToastTypeCode = {
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
  WARNING: "WARNING",
} as const;

export const RoleTypeCode = {
  SuperAdmin: "Super Admin",
  Responder: "Responder",
  User: "User",
  Coordinator: "Coordinator",
  Administrator: "Administrator"
} as const;

export const showToast = (message: string, type: string) => {

  if (type === ToastTypeCode?.SUCCESS) {
    toast.success(message, { position: ToastPosition?.TOP_RIGHT });
  } else if (type === ToastTypeCode?.ERROR) {
    toast.error(message, { position: ToastPosition?.TOP_RIGHT });
  } else {
    console.error("Invalid toast type provided!");
  }
};

export const userIdForPublicSurvey = 2147483647;
export const userIdForPublicSurveyString = "2147483647";

export const superAdminUserId = 1;

export const timezoneToCountry: Record<string, string> = {
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "America/Honolulu": "US",
  "Europe/London": "GB",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Vienna": "AT",
  "Europe/Zurich": "CH",
  "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Budapest": "HU",
  "Europe/Bucharest": "RO",
  "Europe/Sofia": "BG",
  "Europe/Athens": "GR",
  "Europe/Istanbul": "TR",
  "Europe/Moscow": "RU",
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Kuwait": "KW",
  "Asia/Doha": "QA",
  "Asia/Muscat": "OM",
  "Asia/Bahrain": "BH",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Asia/Shanghai": "CN",
  "Asia/Beijing": "CN",
  "Asia/Hong_Kong": "HK",
  "Asia/Singapore": "SG",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID",
  "Asia/Manila": "PH",
  "Asia/Taipei": "TW",
  "Asia/Ho_Chi_Minh": "VN",
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Perth": "AU",
  "Australia/Adelaide": "AU",
  "Pacific/Auckland": "NZ",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Montreal": "CA",
  "America/Edmonton": "CA",
  "America/Winnipeg": "CA",
  "America/Halifax": "CA",
  "America/Mexico_City": "MX",
  "America/Sao_Paulo": "BR",
  "America/Buenos_Aires": "AR",
  "America/Lima": "PE",
  "America/Bogota": "CO",
  "America/Santiago": "CL",
  "America/Caracas": "VE",
  "Africa/Cairo": "EG",
  "Africa/Lagos": "NG",
  "Africa/Johannesburg": "ZA",
  "Africa/Nairobi": "KE",
  "Africa/Casablanca": "MA",
  "Africa/Algiers": "DZ",
  "Africa/Tunis": "TN",
  "Africa/Addis_Ababa": "ET",
  "Africa/Accra": "GH",
  "Africa/Dar_es_Salaam": "TZ",
  "Africa/Kampala": "UG",
  "Africa/Khartoum": "SD",
  "Africa/Lusaka": "ZM",
  "Africa/Harare": "ZW",
  "Africa/Maputo": "MZ",
  "Africa/Windhoek": "NA",
  "Africa/Gaborone": "BW",
  "Africa/Maseru": "LS",
  "Africa/Mbabane": "SZ",
  "Indian/Mauritius": "MU",
  "Indian/Maldives": "MV",
  "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD",
  "Asia/Colombo": "LK",
  "Asia/Kathmandu": "NP",
  "Asia/Kabul": "AF",
  "Asia/Tashkent": "UZ",
  "Asia/Almaty": "KZ",
  "Asia/Baku": "AZ",
  "Asia/Yerevan": "AM",
  "Asia/Tbilisi": "GE",
  "Asia/Tehran": "IR",
  "Asia/Baghdad": "IQ",
  "Asia/Damascus": "SY",
  "Asia/Beirut": "LB",
  "Asia/Amman": "JO",
  "Asia/Jerusalem": "IL",
  "Asia/Nicosia": "CY",
};